import type { Amount } from "../../../core/amount/amount";
import type { TransactionGeolocationManager } from "../../../core/geolocalisation/transaction-geolocalisation-manager";
import { runAfterInteractions } from "../../../core/interaction/interaction-manager";
import { ObservableArray } from "../../../utils/observable-array";
import type { AccountManager } from "../../account/account-manager";
import { accountsAffectedByTransaction } from "../../accounting-transaction/transaction/accounts-affected-by-transaction";
import type { Transaction } from "../../accounting-transaction/transaction/transaction";
import type { TransactionsManager } from "../../accounting-transaction/transaction/transactions-manager";
import type { PincodeSubmission } from "../../pincode/pincode";
import type { TransactionCode } from "../../transactions-codes/transaction-code";
import type { ConfirmationMode } from "../transaction-request";
import type { DedicatedTransfer } from "./dedicated-transfer";
import type { DedicatedTransferService } from "./dedicated-transfer-service";

export class DedicatedTransferManager {
  public transactionCodes = new ObservableArray<TransactionCode>([]);

  public constructor(
    private dedicatedTransferService: DedicatedTransferService,
    private geolocationManager: TransactionGeolocationManager,
    private accountManager: AccountManager,
    private transactionsManager: TransactionsManager,
  ) {}

  public async startTransfer(
    recipientId: string,
    acceptorId: string,
    amount: Amount,
    label: string | undefined = undefined,
  ) {
    try {
      const location = await this.geolocationManager.updatePosition();
      return await this.dedicatedTransferService.startTransfer(recipientId, acceptorId, amount, label, location);
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  public async confirmSendDedicatedTransfer(
    confirmationMode: ConfirmationMode,
    dedicatedTransfer: DedicatedTransfer,
    pincode?: PincodeSubmission,
  ) {
    const location = this.geolocationManager.getPosition();
    const result = await this.dedicatedTransferService.confirmSendDedicatedTransfer(
      confirmationMode,
      dedicatedTransfer,
      pincode,
      location,
    );
    this.refreshAccountAndTransactions(result.metadata.transaction);
    return result;
  }

  private refreshAccountAndTransactions(transaction: Transaction) {
    runAfterInteractions(async () => {
      const accountIds = accountsAffectedByTransaction(transaction);
      await this.accountManager.refresh();
      await this.transactionsManager.refresh(accountIds);
    });
  }
}
